/**
 * Partner Detail — one partner, all their projects (PARTNER SPRINT
 * 2026-05-21). The "Partner Portfolio" the spec names.
 *
 * ─────────────────────────────────────────────────────────────────────────
 *  AVBP_DATA_MODEL_IA_SPEC_v1 §2/§5/Q6 — the channel-partner portfolio.
 * ─────────────────────────────────────────────────────────────────────────
 *  Reached from the Partners list (`/partner/:partnerId`). MIRRORS
 *  `ClientDetail.tsx`. Shows:
 *    · the partner master record — contact details + partnership notes
 *    · the list of THAT partner's projects — every project whose
 *      `partnerId` matches (a partner is linked to N projects)
 *    · money rolled up across all the partner's projects (the billing
 *      rollup the spec §5 names)
 *
 *  One deliberate difference from Client Detail's "+ New Project": a
 *  project's primary owner is its CLIENT, so a new partner-linked project
 *  is started from the Client Detail "+ New Project" flow, where the
 *  partner picker attaches this partner. Partner Detail therefore does NOT
 *  carry its own "+ New Project" button — it carries a contextual note to
 *  that flow instead. Conversation stays per-project (spec §5/§6).
 *
 *  NOT in this sprint (Phase 6): partner login, the co-branded end-client
 *  portal, partner wholesale pricing/billing.
 *
 *  Client never reaches this route (RequireAuth roles gate — admin+staff).
 */
import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { useToast } from '../components/Toast';
import { visibilityFor } from '../lib/roles';
import { Box, Btn3, Card, EmptyNote, StageRail, Tag } from '../components/ui';
import { RelinkProjectPanel } from '../components/RelinkProject';
import {
  inr,
  moneyViewOf,
  railOf,
  templateOf,
  type DemoProject,
} from '../lib/trackDemo';
import {
  archivePartner,
  getPartner,
  listenProjects,
  projectLabel,
  projectsForPartner,
  trashPartner,
  updatePartner,
  type LivePartner,
  type LiveProject,
} from '../lib/projectStore';
import { useClientNames } from '../lib/useClientNames';
import { canManageTrash } from '../lib/trash';

/** A LiveProject → the DemoProject shape the track helpers consume. */
function liveToRow(p: LiveProject): DemoProject {
  return {
    pid: p.pid,
    vp: p.vp,
    projectName: p.projectName,
    clientName: p.clientName,
    clientNo: p.clientNo,
    tier: p.tierCode,
    activeStageKey: p.activeStageKey,
    assigneeUid: p.assigneeUid,
    assigneeName: p.assigneeName,
    valueINR: p.valueINR,
    clearedGateIds: p.clearedGateIds,
    openProposals: 0,
  };
}

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--rule)',
  borderRadius: 'var(--r-sm)',
  padding: '8px 10px',
  fontSize: 13.5,
  fontFamily: 'inherit',
  color: 'var(--ink)',
  background: 'var(--surface)',
  width: '100%',
};

function Lbl({ children }: { children: React.ReactNode }) {
  return (
    <div
      style={{
        fontSize: 12,
        fontWeight: 600,
        color: 'var(--ink-mute)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        margin: '14px 0 8px',
      }}
    >
      {children}
    </div>
  );
}

export default function PartnerDetail() {
  const { partnerId = '' } = useParams();
  const pid = decodeURIComponent(partnerId);
  const { role, user } = useAuth();
  const { toast } = useToast();
  const vis = visibilityFor(role);
  const actorName = user?.displayName || user?.email?.split('@')[0] || 'You';

  const [partner, setPartner] = useState<LivePartner | null>(null);
  const [partnerLoaded, setPartnerLoaded] = useState(false);
  const [live, setLive] = useState<LiveProject[]>([]);
  // Canonical client name — project `clientName` is a display cache that
  // can drift; the clients/{id} master is the single source of truth.
  const { resolveName } = useClientNames();
  // Which project row's RE-LINK panel is open (item 1).
  const [relinking, setRelinking] = useState<string | null>(null);

  // The partner master record.
  useEffect(() => {
    let alive = true;
    getPartner(pid)
      .then((p) => {
        if (!alive) return;
        setPartner(p);
        setPartnerLoaded(true);
      })
      .catch(() => {
        if (!alive) return;
        setPartnerLoaded(true);
      });
    return () => {
      alive = false;
    };
  }, [pid]);

  // The live project stream — filtered to this partner.
  useEffect(() => {
    const unsub = listenProjects(setLive, () =>
      toast('Could not load projects (check connection)'),
    );
    return unsub;
  }, [toast]);

  const projects = useMemo(
    () => projectsForPartner(live, pid),
    [live, pid],
  );
  const visibleProjects = projects.filter((p) => !p.archived);
  const archivedProjects = projects.filter((p) => p.archived);

  // Money rolled up across ALL the partner's live projects (the billing
  // rollup spec §5 names). Same fold ClientDetail uses.
  const rollup = useMemo(() => {
    let value = 0;
    let paid = 0;
    let due = 0;
    for (const p of visibleProjects) {
      const m = moneyViewOf(liveToRow(p));
      value += m.valueINR;
      paid += m.paidINR;
      due += m.dueINR;
    }
    return { value, paid, due };
  }, [visibleProjects]);

  const displayName = partner?.name || pid;
  const displayNo = partner?.partnerNo || pid;

  // A partner id with no master record → genuinely unknown.
  if (partnerLoaded && !partner) {
    return (
      <Box title="Partner" note={pid}>
        <EmptyNote title="Partner not found">
          No partner master exists for “{pid}”.{' '}
          <Link to="/partners">Back to Partners</Link>.
        </EmptyNote>
      </Box>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Box title={displayName} note={`Partner ${displayNo}`}>
        <div
          style={{
            marginBottom: 6,
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <Link to="/partners" style={{ fontSize: 12.5 }}>
            ‹ All partners
          </Link>
          {partner?.archived && <Tag kind="warn">archived</Tag>}
        </div>

        {/* Contact details + partnership notes */}
        <PartnerContact
          partner={partner}
          fallbackName={displayName}
          actorName={actorName}
          mayTrash={canManageTrash(role)}
          onSaved={(p, msg) => {
            setPartner(p);
            toast(msg);
          }}
        />

        {/* Money rolled up across all the partner's projects */}
        {vis.seesMoneyInternals && (
          <>
            <Lbl>Money — across all projects</Lbl>
            <div style={{ display: 'flex', gap: 20, flexWrap: 'wrap' }}>
              <Stat label="Total value" value={inr(rollup.value)} />
              <Stat
                label="Paid"
                value={inr(rollup.paid)}
                tone="var(--good)"
              />
              <Stat label="Due" value={inr(rollup.due)} />
            </div>
          </>
        )}
      </Box>

      {/* The partner's projects */}
      <Box
        title="Projects"
        note={`${visibleProjects.length} active${
          archivedProjects.length
            ? ` · ${archivedProjects.length} archived`
            : ''
        }`}
      >
        <p
          style={{
            fontSize: 11.5,
            color: 'var(--ink-mute)',
            margin: '0 0 14px',
          }}
        >
          A project belongs to a client; to start one for this partner, use
          “+ New project” on a Client Detail screen and pick this partner.
        </p>

        {visibleProjects.length === 0 && archivedProjects.length === 0 && (
          <EmptyNote title="No projects yet">
            No project is linked to this partner. Attach this partner when
            creating a project from a Client Detail screen.
          </EmptyNote>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {[...visibleProjects, ...archivedProjects].map((p) => {
            const row = liveToRow(p);
            const tpl = templateOf(row);
            const m = moneyViewOf(row);
            // Master record wins over the project's cached `clientName`.
            const clientName = resolveName(
              p.clientId || p.clientNo,
              p.clientName,
            );
            return (
              <Card
                key={p.pid}
                className="row-card"
                style={p.archived ? { opacity: 0.6 } : undefined}
              >
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    flexWrap: 'wrap',
                    marginBottom: 10,
                  }}
                >
                  {/* The project's own name leads; VP code + client
                      identity sit beside it. Nameless → VP placeholder. */}
                  <b>{projectLabel(p.projectName, p.vp, p.tierCode)}</b>
                  <span style={{ fontSize: 12, color: 'var(--ink-mute)' }}>
                    {p.vp} · {clientName} · {p.clientNo}
                  </span>
                  <Tag kind="val">
                    {tpl.tier} · {tpl.displayName}
                  </Tag>
                  {p.archived && <Tag kind="warn">archived</Tag>}
                  {vis.seesMoneyInternals && (
                    <span style={{ fontSize: 12 }}>
                      <b style={{ color: 'var(--good)' }}>
                        {inr(m.paidINR)}
                      </b>
                      {' / '}
                      {inr(m.valueINR)}
                    </span>
                  )}
                  <span
                    style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}
                  >
                    <Btn3
                      intent="ghost"
                      size="sm"
                      onClick={() =>
                        setRelinking((r) => (r === p.pid ? null : p.pid))
                      }
                    >
                      {relinking === p.pid ? 'Close' : 'Re-link'}
                    </Btn3>
                    <Link to={`/client/${encodeURIComponent(p.clientId)}`}>
                      <Btn3 intent="ghost" size="sm">
                        Client
                      </Btn3>
                    </Link>
                    <Link to={`/project/${p.pid}`}>
                      <Btn3 intent="ink" size="sm">
                        Open
                      </Btn3>
                    </Link>
                  </span>
                </div>
                <StageRail stages={railOf(row)} />
                {relinking === p.pid && (
                  <RelinkProjectPanel
                    project={{
                      pid: p.pid,
                      vp: p.vp,
                      projectName: p.projectName,
                      clientId: p.clientId || p.clientNo,
                      clientNo: p.clientNo,
                      clientName,
                      partnerId: p.partnerId ?? null,
                    }}
                    actorName={actorName}
                    onDone={(msg) => toast(msg)}
                    onClose={() => setRelinking(null)}
                  />
                )}
              </Card>
            );
          })}
        </div>
      </Box>
    </div>
  );
}

function Stat({
  label,
  value,
  tone,
}: {
  label: string;
  value: string;
  tone?: string;
}) {
  return (
    <div>
      <div
        style={{
          fontSize: 11,
          color: 'var(--ink-mute)',
          textTransform: 'uppercase',
          letterSpacing: '0.04em',
        }}
      >
        {label}
      </div>
      <div
        style={{ fontSize: 16, fontWeight: 600, color: tone || 'var(--ink)' }}
      >
        {value}
      </div>
    </div>
  );
}

/* ════════════════════ Partner contact (view + edit + archive) ═══════════ */

function PartnerContact({
  partner,
  fallbackName,
  actorName,
  mayTrash,
  onSaved,
}: {
  partner: LivePartner | null;
  fallbackName: string;
  actorName: string;
  /** true → the signed-in user may move the partner to / from Trash. */
  mayTrash: boolean;
  onSaved: (p: LivePartner, msg: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(partner?.name || fallbackName);
  const [firmName, setFirmName] = useState(partner?.firmName || '');
  const [phone, setPhone] = useState(partner?.phone || '');
  const [email, setEmail] = useState(partner?.email || '');
  const [website, setWebsite] = useState(partner?.website || '');
  const [location, setLocation] = useState(partner?.location || '');
  const [notes, setNotes] = useState(partner?.notes || '');
  const [confirmArchive, setConfirmArchive] = useState(false);
  const [confirmTrash, setConfirmTrash] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (partner) {
      setName(partner.name || fallbackName);
      setFirmName(partner.firmName);
      setPhone(partner.phone);
      setEmail(partner.email);
      setWebsite(partner.website);
      setLocation(partner.location);
      setNotes(partner.notes);
    }
  }, [partner, fallbackName]);

  async function save() {
    if (busy || !partner) return;
    setBusy(true);
    try {
      await updatePartner(
        partner.partnerNo,
        {
          name: name.trim(),
          firmName: firmName.trim(),
          phone: phone.trim(),
          email: email.trim(),
          website: website.trim(),
          location: location.trim(),
          notes: notes.trim(),
        },
        actorName,
      );
      onSaved(
        {
          ...partner,
          name: name.trim(),
          firmName: firmName.trim(),
          phone: phone.trim(),
          email: email.trim(),
          website: website.trim(),
          location: location.trim(),
          notes: notes.trim(),
        },
        `Saved partner ${partner.partnerNo}.`,
      );
      setEditing(false);
    } catch (e) {
      onSaved(partner, `Save failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function doArchive() {
    if (busy || !partner) return;
    setBusy(true);
    try {
      await archivePartner(partner.partnerNo, actorName, !partner.archived);
      onSaved(
        { ...partner, archived: !partner.archived },
        `Partner ${partner.partnerNo} ${
          partner.archived ? 'restored' : 'archived'
        }.`,
      );
      setConfirmArchive(false);
    } catch (e) {
      onSaved(partner, `Archive failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  async function doTrash() {
    if (busy || !partner) return;
    setBusy(true);
    try {
      await trashPartner(partner.partnerNo, actorName, !partner.trashed);
      onSaved(
        { ...partner, trashed: !partner.trashed },
        partner.trashed
          ? `Partner ${partner.partnerNo} restored from Trash.`
          : `Partner ${partner.partnerNo} moved to Trash (recoverable).`,
      );
      setConfirmTrash(false);
    } catch (e) {
      onSaved(partner, `Trash failed: ${(e as Error).message}`);
    } finally {
      setBusy(false);
    }
  }

  if (!partner) return null;

  if (!editing) {
    return (
      <div style={{ fontSize: 13.5, color: 'var(--ink-soft)', lineHeight: 1.8 }}>
        {partner.firmName && (
          <div style={{ color: 'var(--ink)', fontWeight: 600 }}>
            {partner.firmName}
          </div>
        )}
        <span>{partner.phone || '—'}</span>
        {' · '}
        <span>{partner.email || '—'}</span>
        {' · '}
        <span>{partner.location || '—'}</span>
        {partner.website && (
          <>
            {' · '}
            <a
              href={
                /^https?:\/\//i.test(partner.website)
                  ? partner.website
                  : `https://${partner.website}`
              }
              target="_blank"
              rel="noreferrer"
            >
              {partner.website}
            </a>
          </>
        )}
        <Btn3 intent="ghost" size="sm" onClick={() => setEditing(true)}>
          Edit
        </Btn3>
        {partner.notes && (
          <p
            style={{
              fontSize: 12.5,
              color: 'var(--ink-mute)',
              margin: '6px 0 0',
            }}
          >
            {partner.notes}
          </p>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Contact name"
          style={{ ...inputStyle, flex: 1, minWidth: 150 }}
        />
        <input
          value={firmName}
          onChange={(e) => setFirmName(e.target.value)}
          placeholder="Firm / company name"
          style={{ ...inputStyle, flex: 1, minWidth: 150 }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <input
          value={phone}
          onChange={(e) => setPhone(e.target.value)}
          placeholder="Phone"
          style={{ ...inputStyle, flex: 1, minWidth: 150 }}
        />
        <input
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="Email"
          style={{ ...inputStyle, flex: 1, minWidth: 150 }}
        />
      </div>
      <div style={{ display: 'flex', gap: 8, marginTop: 8, flexWrap: 'wrap' }}>
        <input
          value={website}
          onChange={(e) => setWebsite(e.target.value)}
          placeholder="Website"
          style={{ ...inputStyle, flex: 1, minWidth: 150 }}
        />
        <input
          value={location}
          onChange={(e) => setLocation(e.target.value)}
          placeholder="Location"
          style={{ ...inputStyle, flex: 1, minWidth: 150 }}
        />
      </div>
      <textarea
        value={notes}
        onChange={(e) => setNotes(e.target.value)}
        placeholder="Notes about the partnership"
        rows={2}
        style={{ ...inputStyle, marginTop: 8, resize: 'vertical' }}
      />
      <div
        style={{
          display: 'flex',
          gap: 8,
          marginTop: 12,
          flexWrap: 'wrap',
          alignItems: 'center',
        }}
      >
        <Btn3 intent="grn" size="sm" onClick={save} disabled={busy}>
          {busy ? 'Saving…' : 'Save'}
        </Btn3>
        <Btn3
          intent="ghost"
          size="sm"
          onClick={() => setEditing(false)}
          disabled={busy}
        >
          Cancel
        </Btn3>
        {!confirmArchive && !confirmTrash && (
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setConfirmArchive(true)}
            disabled={busy}
          >
            {partner.archived ? 'Restore…' : 'Archive…'}
          </Btn3>
        )}
        {confirmArchive && (
          <span
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              fontSize: 12,
            }}
          >
            <span style={{ color: 'var(--ink-soft)' }}>
              {partner.archived
                ? 'Restore this partner?'
                : 'Archive — hidden from the default list (reversible, never deleted).'}
            </span>
            <Btn3
              intent="ghost"
              size="sm"
              onClick={() => setConfirmArchive(false)}
            >
              Cancel
            </Btn3>
            <Btn3 intent="ink" size="sm" onClick={doArchive} disabled={busy}>
              {partner.archived ? 'Yes, restore' : 'Yes, archive'}
            </Btn3>
          </span>
        )}
        {/* Move to Trash — recoverable, distinct from archive. */}
        {mayTrash && !confirmArchive && !confirmTrash && (
          <Btn3
            intent="ghost"
            size="sm"
            onClick={() => setConfirmTrash(true)}
            disabled={busy}
          >
            {partner.trashed ? 'Restore from Trash…' : 'Move to Trash…'}
          </Btn3>
        )}
        {confirmTrash && (
          <span
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              fontSize: 12,
            }}
          >
            <span style={{ color: 'var(--ink-soft)' }}>
              {partner.trashed
                ? 'Restore this partner from the Trash?'
                : 'Move to Trash — removed from every list (recoverable from the Trash screen).'}
            </span>
            <Btn3
              intent="ghost"
              size="sm"
              onClick={() => setConfirmTrash(false)}
            >
              Cancel
            </Btn3>
            <Btn3 intent="ink" size="sm" onClick={doTrash} disabled={busy}>
              {partner.trashed ? 'Yes, restore' : 'Yes, move to Trash'}
            </Btn3>
          </span>
        )}
      </div>
    </div>
  );
}
